import { normalizeSearch } from '../text';
import { BR_STATES, onlyDigits } from '../validators';

/**
 * Geografia básica para o frete (puro, sem rede): distância, capitais, UFs e faixas de CEP.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

export type UF = (typeof BR_STATES)[number];
export type BrRegion = 'N' | 'NE' | 'CO' | 'SE' | 'S';

/** Precisão da coordenada: rua (CEP/endereço), centro da cidade, só o estado (pela faixa do CEP) ou nenhuma. */
export type GeoPrecision = 'street' | 'city' | 'state' | 'none';

/** Raio médio da Terra (IUGG), em km. */
const EARTH_RADIUS_KM = 6371.0088;

/** Distância em linha reta (fórmula de haversine), em km. */
export function haversineKm(a: LatLng, b: LatLng): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Arredonda km para 1 casa decimal. */
export const roundKm = (km: number): number => Math.round(km * 10) / 10;

/**
 * Distância de rua estimada = linha reta × fator de rota (detour index).
 * 1,3 é o padrão sugerido para o Brasil; em trajetos urbanos curtos o fator real costuma ser maior.
 */
export function estimateRoadKm(straightKm: number, roadFactor: number): number {
  return roundKm(straightKm * Math.max(1, roadFactor));
}

export const isValidLatLng = (lat: unknown, lng: unknown): boolean =>
  typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng) &&
  Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0);

// ---------------------------------------------------------------- UFs

/** Nome, região e código IBGE (2 dígitos) de cada UF. */
export const UF_INFO: Record<UF, { name: string; region: BrRegion; ibge: string }> = {
  AC: { name: 'Acre', region: 'N', ibge: '12' },
  AL: { name: 'Alagoas', region: 'NE', ibge: '27' },
  AP: { name: 'Amapá', region: 'N', ibge: '16' },
  AM: { name: 'Amazonas', region: 'N', ibge: '13' },
  BA: { name: 'Bahia', region: 'NE', ibge: '29' },
  CE: { name: 'Ceará', region: 'NE', ibge: '23' },
  DF: { name: 'Distrito Federal', region: 'CO', ibge: '53' },
  ES: { name: 'Espírito Santo', region: 'SE', ibge: '32' },
  GO: { name: 'Goiás', region: 'CO', ibge: '52' },
  MA: { name: 'Maranhão', region: 'NE', ibge: '21' },
  MT: { name: 'Mato Grosso', region: 'CO', ibge: '51' },
  MS: { name: 'Mato Grosso do Sul', region: 'CO', ibge: '50' },
  MG: { name: 'Minas Gerais', region: 'SE', ibge: '31' },
  PA: { name: 'Pará', region: 'N', ibge: '15' },
  PB: { name: 'Paraíba', region: 'NE', ibge: '25' },
  PR: { name: 'Paraná', region: 'S', ibge: '41' },
  PE: { name: 'Pernambuco', region: 'NE', ibge: '26' },
  PI: { name: 'Piauí', region: 'NE', ibge: '22' },
  RJ: { name: 'Rio de Janeiro', region: 'SE', ibge: '33' },
  RN: { name: 'Rio Grande do Norte', region: 'NE', ibge: '24' },
  RS: { name: 'Rio Grande do Sul', region: 'S', ibge: '43' },
  RO: { name: 'Rondônia', region: 'N', ibge: '11' },
  RR: { name: 'Roraima', region: 'N', ibge: '14' },
  SC: { name: 'Santa Catarina', region: 'S', ibge: '42' },
  SP: { name: 'São Paulo', region: 'SE', ibge: '35' },
  SE: { name: 'Sergipe', region: 'NE', ibge: '28' },
  TO: { name: 'Tocantins', region: 'N', ibge: '17' },
};

export function isUF(v: unknown): v is UF {
  return typeof v === 'string' && v in UF_INFO;
}

export function toUF(v: string | null | undefined): UF | null {
  const s = (v ?? '').trim().toUpperCase();
  return isUF(s) ? s : null;
}

/** UF pelos 2 primeiros dígitos do código IBGE do município (ex.: 5208707 → GO). */
export function ufFromIbge(code: string | null | undefined): UF | null {
  const prefix = (code ?? '').slice(0, 2);
  if (prefix.length !== 2) return null;
  return (Object.keys(UF_INFO) as UF[]).find((uf) => UF_INFO[uf].ibge === prefix) ?? null;
}

// ---------------------------------------------------------------- Capitais

export interface CapitalInfo {
  ibge: string;
  city: string;
  uf: UF;
  lat: number;
  lng: number;
}

/** As 27 capitais (centróide do município). Fonte: kelvins/municipios-brasileiros (MIT), códigos IBGE. */
export const BR_CAPITALS: readonly CapitalInfo[] = [
  { ibge: '1200401', city: 'Rio Branco', uf: 'AC', lat: -9.97499, lng: -67.8243 },
  { ibge: '2704302', city: 'Maceió', uf: 'AL', lat: -9.66599, lng: -35.735 },
  { ibge: '1302603', city: 'Manaus', uf: 'AM', lat: -3.11866, lng: -60.0212 },
  { ibge: '1600303', city: 'Macapá', uf: 'AP', lat: 0.034934, lng: -51.0694 },
  { ibge: '2927408', city: 'Salvador', uf: 'BA', lat: -12.9718, lng: -38.5011 },
  { ibge: '2304400', city: 'Fortaleza', uf: 'CE', lat: -3.71664, lng: -38.5423 },
  { ibge: '5300108', city: 'Brasília', uf: 'DF', lat: -15.7795, lng: -47.9297 },
  { ibge: '3205309', city: 'Vitória', uf: 'ES', lat: -20.3155, lng: -40.3128 },
  { ibge: '5208707', city: 'Goiânia', uf: 'GO', lat: -16.6864, lng: -49.2643 },
  { ibge: '2111300', city: 'São Luís', uf: 'MA', lat: -2.53874, lng: -44.2825 },
  { ibge: '3106200', city: 'Belo Horizonte', uf: 'MG', lat: -19.9102, lng: -43.9266 },
  { ibge: '5002704', city: 'Campo Grande', uf: 'MS', lat: -20.4486, lng: -54.6295 },
  { ibge: '5103403', city: 'Cuiabá', uf: 'MT', lat: -15.601, lng: -56.0974 },
  { ibge: '1501402', city: 'Belém', uf: 'PA', lat: -1.4554, lng: -48.4898 },
  { ibge: '2507507', city: 'João Pessoa', uf: 'PB', lat: -7.11509, lng: -34.8641 },
  { ibge: '2611606', city: 'Recife', uf: 'PE', lat: -8.04666, lng: -34.8771 },
  { ibge: '2211001', city: 'Teresina', uf: 'PI', lat: -5.09194, lng: -42.8034 },
  { ibge: '4106902', city: 'Curitiba', uf: 'PR', lat: -25.4195, lng: -49.2646 },
  { ibge: '3304557', city: 'Rio de Janeiro', uf: 'RJ', lat: -22.9129, lng: -43.2003 },
  { ibge: '2408102', city: 'Natal', uf: 'RN', lat: -5.79357, lng: -35.1986 },
  { ibge: '1100205', city: 'Porto Velho', uf: 'RO', lat: -8.76077, lng: -63.8999 },
  { ibge: '1400100', city: 'Boa Vista', uf: 'RR', lat: 2.82384, lng: -60.6753 },
  { ibge: '4314902', city: 'Porto Alegre', uf: 'RS', lat: -30.0318, lng: -51.2065 },
  { ibge: '4205407', city: 'Florianópolis', uf: 'SC', lat: -27.5945, lng: -48.5477 },
  { ibge: '2800308', city: 'Aracaju', uf: 'SE', lat: -10.9091, lng: -37.0677 },
  { ibge: '3550308', city: 'São Paulo', uf: 'SP', lat: -23.5329, lng: -46.6395 },
  { ibge: '1721000', city: 'Palmas', uf: 'TO', lat: -10.24, lng: -48.3558 },
];

const CAPITAL_BY_UF = new Map(BR_CAPITALS.map((c) => [c.uf, c]));
const CAPITAL_BY_IBGE = new Map(BR_CAPITALS.map((c) => [c.ibge, c]));

export function capitalOf(uf: UF): CapitalInfo {
  return CAPITAL_BY_UF.get(uf)!;
}

/** Nome de cidade comparável: sem acento, minúsculo, sem espaços repetidos e com hífen como espaço. */
export function normalizeCity(city: string | null | undefined): string {
  return normalizeSearch((city ?? '').replace(/[-'’`]/g, ' '));
}

export interface PlaceRef {
  ibge?: string | null;
  city?: string | null;
  uf?: string | null;
}

/** A cidade é capital do estado? Pelo código IBGE ou, sem ele, pelo nome + UF. */
export function isCapitalCity(p: PlaceRef): boolean {
  if (p.ibge && CAPITAL_BY_IBGE.has(p.ibge)) return true;
  const uf = toUF(p.uf);
  if (!uf || !p.city) return false;
  return normalizeCity(capitalOf(uf).city) === normalizeCity(p.city);
}

/** Mesma cidade? Compara o código IBGE quando os dois têm; senão nome + UF. */
export function isSameCity(a: PlaceRef, b: PlaceRef): boolean {
  if (a.ibge && b.ibge) return a.ibge === b.ibge;
  const ua = toUF(a.uf);
  const ub = toUF(b.uf);
  if (!ua || !ub || ua !== ub || !a.city || !b.city) return false;
  return normalizeCity(a.city) === normalizeCity(b.city);
}

// ---------------------------------------------------------------- CEP

/** CEP só com os 8 números, ou null. */
export function cleanCep(raw: string | null | undefined): string | null {
  const d = onlyDigits(raw);
  return d.length === 8 && !/^0{8}$/.test(d) ? d : null;
}

/**
 * Faixas de CEP por UF (tabela de faixas dos Correios). Usada só como plano B, quando os
 * serviços de CEP estão fora do ar: dá para saber o estado mesmo sem saber a cidade.
 */
const CEP_RANGES: readonly [number, number, UF][] = [
  [1000000, 19999999, 'SP'],
  [20000000, 28999999, 'RJ'],
  [29000000, 29999999, 'ES'],
  [30000000, 39999999, 'MG'],
  [40000000, 48999999, 'BA'],
  [49000000, 49999999, 'SE'],
  [50000000, 56999999, 'PE'],
  [57000000, 57999999, 'AL'],
  [58000000, 58999999, 'PB'],
  [59000000, 59999999, 'RN'],
  [60000000, 63999999, 'CE'],
  [64000000, 64999999, 'PI'],
  [65000000, 65999999, 'MA'],
  [66000000, 68899999, 'PA'],
  [68900000, 68999999, 'AP'],
  [69000000, 69299999, 'AM'],
  [69300000, 69399999, 'RR'],
  [69400000, 69899999, 'AM'],
  [69900000, 69999999, 'AC'],
  [70000000, 72799999, 'DF'],
  [72800000, 72999999, 'GO'],
  [73000000, 73699999, 'DF'],
  [73700000, 76799999, 'GO'],
  [76800000, 76999999, 'RO'],
  [77000000, 77999999, 'TO'],
  [78000000, 78899999, 'MT'],
  [79000000, 79999999, 'MS'],
  [80000000, 87999999, 'PR'],
  [88000000, 89999999, 'SC'],
  [90000000, 99999999, 'RS'],
];

export function ufFromCep(cep: string | null | undefined): UF | null {
  const c = cleanCep(cep);
  if (!c) return null;
  const n = Number(c);
  return CEP_RANGES.find(([from, to]) => n >= from && n <= to)?.[2] ?? null;
}

/** CEP numérico dentro da faixa (inclusive; aceita a faixa invertida). */
export function cepInRange(cep: string | null | undefined, from: string, to: string): boolean {
  const c = cleanCep(cep);
  if (!c) return false;
  const n = Number(c);
  const a = Number(from);
  const b = Number(to);
  return n >= Math.min(a, b) && n <= Math.max(a, b);
}
